import { OCCASION_CONFIG } from "@listou/shared/constants";
import type { GroupPlaceCard, PublicGroupResponse } from "@listou/shared/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { AddEmailBanner } from "../components/add-email";
import { CodeInput } from "../components/code-input";
import { MyPlace } from "../components/group/my-place";
import { PlaceCard } from "../components/group/place-card";
import { SantaReveal } from "../components/group/santa-reveal";
import { WhoAreYouSheet } from "../components/group/who-are-you";
import { Sticker } from "../components/list-page/parts";
import { LogoMark } from "../components/logo";
import { useToast } from "../components/toast";
import { Dialog } from "../components/ui/dialog";
import { PageLoader } from "../components/ui/spinner";
import { ApiError, authApi, publicGroupsApi } from "../lib/api";
import { rememberSharedGroup } from "../lib/attribution";
import { countdownLabel, formatDay, formatPrice } from "../lib/format";
import { groupSticker, STICKER } from "../lib/group-look";
import { isWatching, setWatching } from "../lib/group-watch-store";
import { seo } from "../lib/head";
import { publicGroupQuery } from "../lib/queries";
import { errorMessage, viewerQuery } from "../lib/session";

export const Route = createFileRoute("/g/$slug")({
  staticData: { ssr: true },
  loader: async ({ context: { queryClient }, params: { slug } }) => {
    try {
      return await queryClient.query(publicGroupQuery(slug));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) throw notFound();
      return null;
    }
  },
  notFoundComponent: GroupMissing,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { group, places } = loaderData;
    return seo({
      title: `${group.title} · Listou`,
      description:
        group.mode === "secret_santa"
          ? `Le Secret Santa organisé par ${group.organizerName}. Choisissez votre prénom pour découvrir à qui vous offrez.`
          : `${places.length} listes d'envies réunies par ${group.organizerName}. Choisissez votre prénom et ajoutez vos idées.`,
      noindex: true,
    });
  },
  component: GroupPage,
});

/** What a claim ran into, when it needs the visitor to decide or prove something. */
type FollowUp =
  | { kind: "code"; place: GroupPlaceCard }
  | { kind: "taken_santa"; place: GroupPlaceCard }
  | { kind: "merge"; place: GroupPlaceCard; token?: string }
  | { kind: "conflict"; place: GroupPlaceCard; token?: string };

function GroupPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const query = useQuery({ ...publicGroupQuery(slug), retryOnMount: false });
  useEffect(() => rememberSharedGroup(slug), [slug]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [followUp, setFollowUp] = useState<FollowUp | null>(null);
  const [linkToken, setLinkToken] = useState<{ placeId: string; token: string } | null>(null);
  const [unwrapping, setUnwrapping] = useState(false);

  const settle = (body: PublicGroupResponse) => {
    queryClient.setQueryData(publicGroupQuery(slug).queryKey, body);
    // A claim may have opened a session, or moved one: whatever is cached about "me" is old.
    void queryClient.invalidateQueries({ queryKey: viewerQuery.queryKey });
    void queryClient.invalidateQueries({ queryKey: ["lists"] });
  };

  // Everything that can fail with "prove it", "merge?" or "someone else here" lands here.
  const onClaimError = (error: unknown, place: GroupPlaceCard | undefined, token?: string) => {
    if (!(error instanceof ApiError) || !place) {
      // A dead personal link (rotated, or mangled in a copy): pick the name instead.
      if (error instanceof ApiError && error.status === 404 && token) {
        toast("Ce lien ne marche plus. Choisissez votre prénom.", "error");
        setSheetOpen(true);
        return;
      }
      toast(errorMessage(error, "Ça n'a pas marché. Réessayez."), "error");
      return;
    }
    setSheetOpen(false);
    switch (error.code) {
      case "place_code_required":
        setFollowUp({ kind: "code", place });
        return;
      case "place_taken_santa":
        setFollowUp({ kind: "taken_santa", place });
        return;
      case "merge_available":
        setFollowUp({ kind: "merge", place, token });
        return;
      case "session_conflict":
        setFollowUp({ kind: "conflict", place, token });
        return;
      case "sign_in_required":
        void navigate({ to: "/login", search: { redirectTo: `/g/${slug}` } });
        return;
      default:
        toast(error.message, "error");
        setSheetOpen(true);
    }
  };

  const claim = useMutation({
    mutationFn: ({ place, merge }: { place: GroupPlaceCard; merge?: boolean }) =>
      publicGroupsApi.claim(slug, place.id, { merge }),
    onSuccess: (body) => {
      settle(body);
      setSheetOpen(false);
      setFollowUp(null);
    },
    onError: (error, { place }) => onClaimError(error, place),
  });

  const open = useMutation({
    mutationFn: ({ placeId, token, merge }: { placeId: string; token: string; merge?: boolean }) =>
      publicGroupsApi.open(slug, { placeId, token, merge }),
    onSuccess: (body) => {
      settle(body);
      setFollowUp(null);
      setLinkToken(null);
      // The personal link proves who this is, so there is no wrong name to guard against:
      // the gift opens straight away. Opening it, not arriving, is what reveals the draw.
      if (body.me?.santa?.state === "ready") setUnwrapping(true);
    },
    onError: (error, { placeId, token }) =>
      onClaimError(
        error,
        query.data?.places.find((place) => place.id === placeId),
        token,
      ),
  });

  const addSelf = useMutation({
    mutationFn: (name: string) => publicGroupsApi.addSelf(slug, { name }),
    onSuccess: (body) => {
      settle(body);
      setSheetOpen(false);
    },
    onError: (error) => toast(errorMessage(error, "Ça n'a pas marché."), "error"),
  });

  const reveal = useMutation({
    mutationFn: () => publicGroupsApi.reveal(slug),
    onSuccess: settle,
    onError: (error) => {
      setUnwrapping(false);
      toast(errorMessage(error, "Le tirage n'a pas pu s'ouvrir."), "error");
    },
  });

  const notMe = useMutation({
    mutationFn: () => publicGroupsApi.unbind(slug),
    onSuccess: (body) => {
      settle(body);
      setSheetOpen(true);
    },
    onError: (error) => toast(errorMessage(error, "Ça n'a pas marché."), "error"),
  });

  // The personal link's token rides in the fragment, which only this browser sees. Read
  // once after mounting, then wiped from the address bar so it is not shared by accident.
  // Remembered in a ref: the effect below runs in the same pass, after the hash is gone,
  // and must still know this visitor came with a link rather than ask who they are.
  const arrivedWithLink = useRef(false);
  useEffect(() => {
    const match = window.location.hash.match(/^#p=([0-9a-f-]{36})\.([\w-]{43})$/);
    if (!match?.[1] || !match[2]) return;
    arrivedWithLink.current = true;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setLinkToken({ placeId: match[1], token: match[2] });
    open.mutate({ placeId: match[1], token: match[2] });
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask who this is once the page is interactive: never during the server render, never
  // for someone who came with their link, and never for someone who chose to only look.
  // Once the page knows who this is, however they got there, the question is gone.
  const me = query.data?.me;
  useEffect(() => {
    if (me) {
      setSheetOpen(false);
      return;
    }
    // A collection is one person's lists: nobody is asked who they are.
    if (
      query.data &&
      query.data.group.mode !== "collection" &&
      !arrivedWithLink.current &&
      !isWatching(slug)
    ) {
      setSheetOpen(true);
    }
  }, [query.data, me, slug]);

  if (query.isPending) return <PageLoader />;
  if (query.isError) return <GroupMissing />;

  const { group, places } = query.data;
  const countdown = countdownLabel(group.eventDate);
  const busy = claim.isPending || open.isPending || addSelf.isPending;

  const share = async () => {
    const url = `${window.location.origin}/g/${slug}`;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: group.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast("Lien copié");
    } catch {
      // The share sheet was closed.
    }
  };

  return (
    <>
      <div className="border-b-2 border-line bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <LogoMark className="size-8 shrink-0" />
            <span className="text-muted">
              Groupe organisé avec <strong className="font-display text-ink">Listou</strong>
            </span>
          </Link>
          {group.organizerGroupId && (
            <Link
              to="/mes-groupes/$groupId"
              params={{ groupId: group.organizerGroupId }}
              className="btn-secondary btn-sm"
            >
              Gérer le groupe
            </Link>
          )}
        </div>
      </div>

      <main className="mx-auto grid max-w-6xl gap-10 px-4 py-8 sm:px-6 sm:py-12">
        <header className="relative grid gap-2 pr-20 sm:pr-32">
          <Sticker
            src={groupSticker(group.occasion, STICKER.gift)}
            className="absolute top-0 right-0 w-20 rotate-12 sm:w-28"
          />
          {(group.mode === "secret_santa" || group.occasion !== "other" || countdown) && (
            <p className="font-bold text-accent-text">
              {group.mode === "secret_santa"
                ? "Secret Santa"
                : group.occasion !== "other"
                  ? OCCASION_CONFIG[group.occasion].label
                  : ""}
              {countdown &&
                `${group.mode === "secret_santa" || group.occasion !== "other" ? ", " : ""}${countdown.toLowerCase()}`}
            </p>
          )}
          <h1 className="text-4xl font-semibold sm:text-6xl">{group.title}</h1>
          <p className="text-lg text-muted">
            {group.mode === "collection" ? "Les listes de " : "Organisé par "}
            {group.organizerName}
            {group.eventDate && `, pour le ${formatDay(group.eventDate)}`}.
            {group.santa?.budgetCents
              ? ` Budget : ${formatPrice(group.santa.budgetCents)} par cadeau.`
              : ""}
          </p>
          {group.santa?.message && (
            <figure className="mt-3 grid max-w-2xl gap-1">
              <blockquote className="font-hand text-3xl leading-snug">
                « {group.santa.message} »
              </blockquote>
              <figcaption className="text-muted">{group.organizerName}</figcaption>
            </figure>
          )}
        </header>

        {me ? (
          <>
            <MyPlace
              group={group}
              me={me}
              mine={places.find((place) => place.isMe) ?? null}
              receiver={places.find((place) => place.id === me.santa?.receiver?.placeId) ?? null}
              fallbackSticker={(index) => groupSticker(group.occasion, index + 2)}
              pending={reveal.isPending || notMe.isPending}
              onReveal={() => setUnwrapping(true)}
              onNotMe={() => notMe.mutate()}
            />
            {me.isGuest && <AddEmailBanner />}
          </>
        ) : group.mode === "collection" ? null : (
          <section className="relative flex flex-wrap items-center justify-between gap-4 rounded-[1.75rem] bg-butter/35 p-6">
            <div className="grid gap-1">
              <h2 className="text-2xl font-semibold">Vous êtes dans le groupe ?</h2>
              <p className="text-ink/75">Choisissez votre prénom pour ajouter vos idées.</p>
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setWatching(slug, false);
                setSheetOpen(true);
              }}
            >
              Choisir mon prénom
            </button>
          </section>
        )}

        <section className="grid gap-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-3xl font-semibold">Les listes</h2>
            {/* A Secret Santa reaches people by their own links; only a list group spreads by its link. */}
            {group.mode === "lists" && (
              <button type="button" className="btn-secondary btn-sm" onClick={() => void share()}>
                Partager le groupe
              </button>
            )}
          </div>
          <ul className="grid gap-5 pt-1 sm:grid-cols-2 lg:grid-cols-3">
            {places.map((place, index) => (
              <li key={place.id}>
                <PlaceCard
                  place={place}
                  people={group.mode !== "collection"}
                  fallbackSticker={(tile) => groupSticker(group.occasion, index + tile + 2)}
                />
              </li>
            ))}
          </ul>
        </section>
      </main>

      <WhoAreYouSheet
        open={sheetOpen}
        places={places}
        canSelfAdd={group.canSelfAdd}
        pending={busy}
        onPick={(place) => {
          // A personal link already named this place: use it rather than the name.
          if (linkToken?.placeId === place.id) open.mutate(linkToken);
          else claim.mutate({ place });
        }}
        onAddSelf={(name) => addSelf.mutate(name)}
        onWatch={() => {
          setWatching(slug, true);
          setSheetOpen(false);
        }}
      />

      {unwrapping && me && (
        <SantaReveal
          name={me.name}
          receiver={me.santa?.receiver?.name ?? null}
          names={places.filter((place) => !place.isMe).map((place) => place.name)}
          stickers={Array.from({ length: 9 }, (_, index) =>
            groupSticker(group.occasion, index),
          ).filter((src) => src !== null)}
          gift={groupSticker(group.occasion, STICKER.gift)}
          onOpen={() => reveal.mutate()}
          onClose={() => setUnwrapping(false)}
        />
      )}
      {followUp?.kind === "code" && (
        <PlaceCodeDialog
          slug={slug}
          place={followUp.place}
          onDone={settle}
          onClose={() => setFollowUp(null)}
        />
      )}
      {followUp?.kind === "taken_santa" && (
        <TakenSantaDialog
          slug={slug}
          place={followUp.place}
          organizerName={group.organizerName}
          onClose={() => setFollowUp(null)}
        />
      )}
      {followUp?.kind === "merge" && (
        <Dialog
          open
          onClose={() => setFollowUp(null)}
          title={`Rattacher ${followUp.place.name} à votre compte ?`}
          description={`${followUp.place.name} a été choisi sans compte, sur un autre appareil. Si c'était vous, sa liste passe sur votre compte.`}
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => {
                const { place, token } = followUp;
                if (token) open.mutate({ placeId: place.id, token, merge: true });
                else claim.mutate({ place, merge: true });
              }}
            >
              Oui, c'était moi
            </button>
            <button type="button" className="btn-ghost" onClick={() => setFollowUp(null)}>
              Non
            </button>
          </div>
        </Dialog>
      )}
      {followUp?.kind === "conflict" && (
        <Dialog
          open
          onClose={() => setFollowUp(null)}
          title={`Continuer comme ${followUp.place.name} ?`}
          description="Ce navigateur est déjà quelqu'un d'autre dans le groupe. Continuer vous déconnecte de cette place-là."
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={async () => {
                const { place, token } = followUp;
                await authApi.logout().catch(() => undefined);
                queryClient.clear();
                if (token) open.mutate({ placeId: place.id, token });
                else claim.mutate({ place });
              }}
            >
              Continuer comme {followUp.place.name}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setFollowUp(null)}>
              Annuler
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

/** The place belongs to an account: its holder proves it with the code mailed to them. */
function PlaceCodeDialog({
  slug,
  place,
  onDone,
  onClose,
}: {
  slug: string;
  place: GroupPlaceCard;
  onDone: (body: PublicGroupResponse) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState("");
  const send = useMutation({ mutationFn: () => publicGroupsApi.sendCode(slug, place.id) });
  const verify = useMutation({
    mutationFn: () => publicGroupsApi.verifyCode(slug, place.id, { code }),
    onSuccess: (body) => {
      onDone(body);
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`C'est vous, ${place.name} ?`}
      description={`${place.name} a un compte Listou. On envoie un code à son adresse pour le confirmer.`}
    >
      {!send.isSuccess ? (
        <button
          type="button"
          className="btn"
          disabled={send.isPending}
          onClick={() => send.mutate()}
        >
          {send.isPending ? "Envoi…" : "M'envoyer le code"}
        </button>
      ) : (
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            verify.mutate();
          }}
        >
          <p>Tapez le code reçu par e-mail.</p>
          <CodeInput
            value={code}
            onChange={setCode}
            onComplete={() => verify.mutate()}
            invalid={verify.isError}
            disabled={verify.isPending}
          />
          {verify.isError && (
            <p className="text-danger">{errorMessage(verify.error, "Ce code ne marche pas.")}</p>
          )}
          <button type="submit" className="btn" disabled={verify.isPending}>
            Confirmer
          </button>
        </form>
      )}
    </Dialog>
  );
}

/**
 * A drawn Secret Santa: a name already taken does not open on a click, or anyone could
 * read Paul's draw. The link goes to Paul's own address instead, or the organizer has it.
 */
function TakenSantaDialog({
  slug,
  place,
  organizerName,
  onClose,
}: {
  slug: string;
  place: GroupPlaceCard;
  organizerName: string;
  onClose: () => void;
}) {
  const mail = useMutation({ mutationFn: () => publicGroupsApi.mailLink(slug, place.id) });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${place.name} a déjà sa place`}
      description="Pour garder le secret, on n'ouvre pas un tirage sur un simple clic."
    >
      {mail.isSuccess ? (
        <p className="text-lg">
          C'est parti : le lien de {place.name} vient d'être envoyé à son adresse.
        </p>
      ) : (
        <div className="grid gap-3">
          {place.canMailLink && (
            <button
              type="button"
              className="btn"
              disabled={mail.isPending}
              onClick={() => mail.mutate()}
            >
              Envoyer son lien par e-mail
            </button>
          )}
          <p className="text-muted">
            {place.canMailLink
              ? `Le lien part à l'adresse de ${place.name}, pas ici. Sinon, ${organizerName} peut vous le renvoyer.`
              : `Demandez votre lien personnel à ${organizerName} : il ou elle peut le copier pour vous.`}
          </p>
          {mail.isError && (
            <p className="text-danger">{errorMessage(mail.error, "L'envoi a échoué.")}</p>
          )}
        </div>
      )}
    </Dialog>
  );
}

function GroupMissing() {
  return (
    <main className="mx-auto grid max-w-md justify-items-start gap-4 px-4 py-24">
      <LogoMark className="size-12" />
      <h1 className="text-4xl font-semibold">Ce groupe n'est pas disponible</h1>
      <p className="text-lg text-muted">
        Le lien a peut-être changé. Demandez le nouveau lien à la personne qui vous l'a envoyé.
      </p>
      <Link to="/" className="btn">
        Découvrir Listou
      </Link>
    </main>
  );
}
